import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import './Management.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:1337';

const ProductManagement = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
  });

  useEffect(() => {
    fetchProducts();
  }, [token]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/products?populate=*`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      setProducts(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = editingId ? 'PUT' : 'POST';
      const endpoint = editingId ? `${API_URL}/api/products/${editingId}` : `${API_URL}/api/products`;

      // Build description in blocks format for Strapi
      const descriptionBlocks = formData.description
        ? [
            {
              type: 'paragraph',
              children: [
                {
                  type: 'text',
                  text: formData.description,
                },
              ],
            },
          ]
        : [];

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            name: formData.name,
            description: descriptionBlocks,
            price: parseFloat(formData.price),
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Save Error:', errorData);
        throw new Error(errorData.message || 'Failed to save product');
      }
      await fetchProducts();
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', description: '', price: '' });
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    }
  };

  const getDescriptionText = (desc) => {
    if (!desc) return '';
    if (typeof desc === 'string') return desc;
    if (Array.isArray(desc)) {
      return desc
        .map(block => {
          if (block.children && Array.isArray(block.children)) {
            return block.children.map(child => child.text || '').join(' ');
          }
          return '';
        })
        .join(' ');
    }
    return '';
  };

  const handleDelete = async (id) => {
    if (!confirm('Weet je zeker dat je dit product wilt verwijderen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/products/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete product');
      await fetchProducts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (product) => {
    const attrs = product.attributes || product;
    setFormData({
      name: attrs.name || '',
      description: getDescriptionText(attrs.description),
      price: attrs.price || '',
    });
    setEditingId(product.id);
    setShowForm(true);
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Productbeheer</h1>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Annuleren' : '+ Product Toevoegen'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Productnaam</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Beschrijving</label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows="4"
            />
          </div>
          <div className="form-group">
            <label>Prijs</label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              required
            />
          </div>
          <button type="submit" className="btn-submit">
            {editingId ? 'Product Bijwerken' : 'Product Aanmaken'}
          </button>
        </form>
      )}

      {loading ? (
        <p className="loading">Producten worden geladen...</p>
      ) : products.length === 0 ? (
        <p className="empty">Geen producten gevonden</p>
      ) : (
        <div className="items-table">
          <table>
            <thead>
              <tr>
                <th>Naam</th>
                <th>Prijs</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => {
                const attrs = product.attributes || product;
                return (
                  <tr key={product.id}>
                    <td>{attrs.name || ''}</td>
                    <td>€{parseFloat(attrs.price || 0).toFixed(2)}</td>
                    <td className="actions">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(product)}
                      >
                        Bewerken
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(product.id)}
                      >
                        Verwijderen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
